import React, { JSX, useEffect } from "react";
import Router from "next/router";
import NProgress from "nprogress";
import classnames from "classnames";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import App, {
  AppProps,
  AppInitialProps,
  AppContext as NextAppContext,
} from "next/app";
import Head from "next/head";
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from "@apollo/client";
import Script from "next/script";
import { endpoints, gtmContainerId, headerPromotion, isLocalEnv } from "config";
import { AppContext } from "context/app";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { CartContext } from "context/cart";
import { AnalyticsSessionParamsContext } from "context/analyticsSessionParams";
import { useAppProvider } from "context/hooks/useAppProvider";
import { useUserProvider } from "context/hooks/useUserProvider";
import { useCartProvider } from "context/hooks/useCartProvider";
import { useModalProvider } from "context/hooks/useModalProvider";
import { useAnalyticsSessionParamsProvider } from "context/hooks/useAnalyticsSessionParamsProvider";
import { Ccm } from "@pepdirect/v3/ui/Ccm";
import { shouldForwardProp } from "@pepdirect/v3/helpers/transientProps";

import "styles/index.scss";

/* Customizing swiper */
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/zoom";
import "styles/swiper.scss";

/* Customizing modal */
import "styles/modal.scss";

import { PoppiTheme } from "@pepdirect/v3/theme/PoppiTheme";
import { useFontTracking } from "@pepdirect/v3/hooks/useFontTracking";
import { FontTrackingiFrame } from "@pepdirect/v3/ui/FontTrackingiFrame";
import { NavHeader } from "components/Header";
import { NavFooter } from "components/Footer";
import { RootModal } from "components/Modals/Modal";
import { SmartCommerceAssets } from "components/SmartCommerce";
import { RouteSeo } from "components/Seo/RouteSeo";

/* Importing and customizing nprogress */
import "styles/nprogress.scss";
import { PRIMARY_COLOR } from "constants/meta";

import "styles/stylingOverrides.scss";
// live's poppi theme for the click2cart widgets (PDP smart buttons + carousel)
import "styles/smartcommerce.scss";
import { useZipCodeProvider } from "context/hooks/useZipCodeProvider";
import { ZipCodeContext } from "context/zipCode";
import { useLogPageProvider } from "hooks/useLogPageView";
import { LogContext } from "context/log";
import { addGitShaToWindow } from "@pepdirect/v3/helpers/gitSha";
import client from "services/graphql/client";
import {
  CurrentUserDocument,
  CurrentUserQuery,
  TenantDocument,
  TenantQuery,
  TenantQueryVariables,
} from "services/graphql/generated";
import createSsrClient from "services/graphql/ssr-client";
import { isServerSide } from "helpers/routing";
addGitShaToWindow();

// Init progress bar
NProgress.configure({ showSpinner: false });
Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// Scroll to top after router.push();
Router.events.on("routeChangeComplete", () => window?.scrollTo(0, 0));

interface PoppiAppInitialProps extends AppInitialProps {
  tenant: TenantQuery;
}

interface PoppiAppProps extends AppProps {
  tenant: TenantQuery;
}

// Pages can opt out of the global header/footer (e.g. the full-bleed team
// login screen) by setting `hideChrome = true` on the page component.
type PageWithChrome = PoppiAppProps["Component"] & { hideChrome?: boolean };

Poppi.getInitialProps = async (
  appContext: NextAppContext
): Promise<PoppiAppInitialProps> => {
  let gqlClient: ApolloClient<NormalizedCacheObject>;
  let userId: CurrentUserQuery["currentUser"] | null = null;
  if (isServerSide(appContext)) {
    // by the if check above, we guarantee that appContext will have request in it.
    gqlClient = createSsrClient(appContext?.ctx?.req);
  } else {
    gqlClient = client;
  }

  try {
    const userData = await gqlClient.query<CurrentUserQuery>({
      query: CurrentUserDocument,
    });
    userId = userData?.data?.currentUser?.id || null;
  } catch (e) {
    // logged in errorLink
  }

  // When we add cookies to the request for tenant query,
  // if the cookie is not valid, then they return 401 unauthenticated error.
  // To prevent this, we detach cookies from the request before making request to them
  // if the user cookie is not valid. If cookie is not set, then this would not be a problem.
  if (!userId && isServerSide(appContext)) {
    gqlClient = createSsrClient(appContext?.ctx?.req, true);
  }

  // On the server (SSR / hard reload) keep strict behavior: let a failed
  // tenant/page query surface as a 500 so real errors stay visible.
  if (isServerSide(appContext)) {
    const [appProps, tenantData] = await Promise.all([
      // calls page's `getInitialProps` and fills `appProps.pageProps`
      App.getInitialProps(appContext),
      gqlClient.query<TenantQuery, TenantQueryVariables>({
        query: TenantDocument,
      }),
    ]);

    return {
      ...appProps,
      tenant: tenantData.data,
    };
  }

  // On the client, a transient network error during a route transition (e.g.
  // an in-flight request aborted by the navigation) surfaces as "Failed to
  // fetch". Re-throwing turns a recoverable hiccup into a full error page +
  // unhandledRejection — Next re-invokes this App-level getInitialProps while
  // rendering `_error`, so the failing query just fires again. Instead we
  // degrade gracefully per request; the UI already handles a missing tenant
  // (title/theme fall back to defaults) and pages can render their empty state.
  const [appProps, tenantData] = await Promise.all([
    App.getInitialProps(appContext).catch((e) => {
      if (isLocalEnv) {
        console.warn(
          `[nav] page getInitialProps failed: ${(e as Error)?.message ?? e}`
        );
      }
      return { pageProps: {} };
    }),
    gqlClient
      .query<TenantQuery, TenantQueryVariables>({ query: TenantDocument })
      .catch((e) => {
        if (isLocalEnv) {
          console.warn(
            `[nav] tenant query failed: ${(e as Error)?.message ?? e}`
          );
        }
        return null;
      }),
  ]);

  return {
    ...appProps,
    tenant: tenantData?.data ?? ({} as TenantQuery),
  };
};

// Revisit this later if layout needs to change https://adamwathan.me/2019/10/17/persistent-layout-patterns-in-nextjs/
export default function Poppi({
  Component,
  pageProps,
  tenant,
  router,
}: PoppiAppProps): JSX.Element {
  const fonts = tenant.tenant?.brandConfig.trackingFonts || [];
  const title = tenant.tenant?.brandConfig.title || "poppi";
  const trackFontResources = useFontTracking(fonts, title);

  useEffect(() => {
    // exposes pod hostname to browser window
    if (window) {
      window._pepdirect = {
        ...window._pepdirect,
        hostname: process.env.HOSTNAME || "",
      };
    }
  }, []);

  const { appContextValue } = useAppProvider(tenant.tenant);
  const { modalContextValue } = useModalProvider();
  const { userContextValue } = useUserProvider(client);
  const { zipCodeContextValue } = useZipCodeProvider();
  const { analyticsParamsValue } = useAnalyticsSessionParamsProvider();
  const { useLogPageView } = useLogPageProvider(userContextValue.currentUserId);
  const { cartContextValue } = useCartProvider(
    client,
    userContextValue.currentUserId
  );

  const hideChrome = Boolean((Component as PageWithChrome).hideChrome);

  const pageWrapperClasses = classnames(
    { "page-wrapper": !hideChrome },
    { "with-promo": !hideChrome && headerPromotion?.shouldShow }
  );

  return (
    <>
      <Head>
        {/* initial-scale=1 matches prod and avoids the iOS orientation-zoom quirk */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={PRIMARY_COLOR} />
      </Head>
      <RouteSeo
        asPath={router.asPath}
        pathname={router.pathname}
        pageProps={pageProps}
      />
      <a className="skip-to-content-link" href="#main-content">
        Skip to content
      </a>
      <SmartCommerceAssets />
      {/* GTM container — the standard bootstrap, verbatim; the required
          noscript iframe lives in _document (right after <body>). */}
      <Script id="gtm-init" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmContainerId}');`}
      </Script>
      {/* TrustArc cookie-consent banner (platform Ccm).*/}
      <Ccm cdn={endpoints.cdn} />
      {/* poppi brand fonts (Greycliff CF for body, Toroka for headlines) are
          declared in styles/fonts.scss. We intentionally do not inject the CMS
          @font-face set here so the local faces are authoritative for the
          poppi rebrand. */}
      {trackFontResources.map((resource) => (
        <FontTrackingiFrame
          resource={resource}
          key={resource}
          isLocalEnv={isLocalEnv}
        />
      ))}
      <ApolloProvider client={client}>
        <AppContext.Provider value={appContextValue}>
          <ThemeProvider theme={PoppiTheme}>
            <ModalContext.Provider value={modalContextValue}>
              <UserContext.Provider value={userContextValue}>
                <CartContext.Provider value={cartContextValue}>
                  <LogContext.Provider value={{ useLogPageView }}>
                    <ZipCodeContext.Provider value={zipCodeContextValue}>
                      <AnalyticsSessionParamsContext.Provider
                        value={analyticsParamsValue}
                      >
                        <StyleSheetManager
                          shouldForwardProp={shouldForwardProp}
                        >
                          <div className="page-layout">
                            {!hideChrome && <NavHeader />}
                            <main
                              id="main-content"
                              className={pageWrapperClasses}
                              tabIndex={-1}
                            >
                              <Component {...pageProps} />
                            </main>
                            {!hideChrome && (
                              <div>
                                <NavFooter />
                              </div>
                            )}
                          </div>
                          <RootModal />
                        </StyleSheetManager>
                      </AnalyticsSessionParamsContext.Provider>
                    </ZipCodeContext.Provider>
                  </LogContext.Provider>
                </CartContext.Provider>
              </UserContext.Provider>
            </ModalContext.Provider>
          </ThemeProvider>
        </AppContext.Provider>
      </ApolloProvider>
    </>
  );
}
